import React, { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { fetcher } from '@dashboard/services/request';
import { couponKey } from '@dashboard/lib/queryKeys';
import {
  initialScope,
  scopeFields,
  scopeRules,
  scopeSchema,
} from '@dashboard/components/DiscountsCoupons/couponScope';
import {
  expiryFields,
  expiryRedeemBy,
  initialExpiry,
} from '@dashboard/components/DiscountsCoupons/couponExpiry';
import * as Yup from 'yup';

const CouponEditDialogLabs = ({ open, onClose, coupon }) => {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();

  const updateCoupon = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/coupons/${coupon._id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKey(coupon._id) });
      toast({
        title: 'Coupon updated',
        status: 'success',
      });
      onClose();
    },
    onError: error => {
      toast({
        title: 'Failed to update coupon',
        description: error?.message || 'Please try again or contact support.',
        status: 'error',
      });
    },
  });

  const scope = useMemo(
    () => initialScope({ pages: coupon.pages, rules: coupon.rules }),
    [coupon.pages, coupon.rules],
  );

  return (
    <Dialog
      title="Edit coupon"
      cancelButton={null}
      confirmButton={null}
      size="xl"
      open={open}
      onCancel={onClose}
    >
      {() => (
        <Form
          validationSchema={scopeSchema.shape({
            maxRedemptions: Yup.number().min(0, 'Max redemptions must be at least 0').optional(),
          })}
          showState={false}
          initialValues={{
            label: coupon.label,
            ...scope,
            maxRedemptions: coupon.maxRedemptions ?? '',
            ...initialExpiry(coupon.redeemBy),
          }}
          onSubmit={async values => {
            try {
              // An empty list clears the limit; leaving it out would keep the old one.
              const submitData = { rules: scopeRules(values) };

              if (values.maxRedemptions !== '') {
                submitData.maxRedemptions = Number(values.maxRedemptions);
              }

              // Left out, the API clears the expiry: that is how Doesn't expire saves.
              const redeemBy = expiryRedeemBy(values);
              if (redeemBy) submitData.redeemBy = redeemBy;

              submitData.label = values.label;

              await updateCoupon.mutateAsync(submitData);
              return { data: true };
            } catch (error) {
              return { error };
            }
          }}
          fields={({ values }) => [
            {
              name: 'label',
              label: 'Label',
              required: true,
              type: 'text',
              placeholder: 'Coupon label',
              helpText: 'Internal name for this coupon.',
            },
            { type: 'divider', id: 'scope-divider' },
            ...scopeFields({ visible: true, limitScope: values.limitScope }),
            { type: 'divider', id: 'limits-divider' },
            {
              type: 'number',
              placeholder: 'Unlimited',
              label: 'Max uses',
              helpText: 'Total uses across all customers. One customer can use it more than once.',
              name: 'maxRedemptions',
            },
            ...expiryFields({ visible: true, values }),
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Save',
            appearance: 'primary',
            size: 'full',
          }}
        />
      )}
    </Dialog>
  );
};

export default CouponEditDialogLabs;
